import { FC, useCallback, useEffect } from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/appointments/v3/model.pb';
import { AppointmentsApiQueries } from '@frontend/api-appointments';
import { AppointmentSelectorComponents } from '@frontend/appointment-selector';
import { BadgeBanner } from '@frontend/components';
import { EditableText } from '@frontend/editable-text';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  Accordion,
  AutoGrowTextarea,
  BannerNotification,
  ContentLoader,
  Text,
  useForm,
  BaseFieldProps,
  ValidatorFieldState,
} from '@frontend/design-system';
import { pendoTags } from '../../../constants';
import { useSendFormsStore } from '../../../providers';
import {
  accordionBodyStyle,
  accordionHeaderStyle,
  accordionItemStyle,
  appointmentsContainerStyle,
  bannerNotificationStyle,
  noteInputStyle,
  noteTitleStyle,
  textStyle,
  tileHeaderContentStyle,
  titleStyle,
  titleWithBadgeWrapperStyle,
  titleTextStyle,
} from './appointment-selection.styles';
import AttachDocuments from './attach-documents/attach-documents.component';

const EMPTY_APPOINTMENTS: Appointment[] = [];
const MAX_INTERNAL_NOTE_TITLE_LENGTH = 45;
const INVALID_INTERNAL_NOTE_TITLE_CHARACTERS_REGEX = /[^a-zA-Z0-9 _\-()]/;
const MAX_INTERNAL_NOTE_LENGTH = 1024;

const Appointments: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const { personId, locationId, setAppointmentId, setAppointmentInfo } = useSendFormsStore([
    'personId',
    'locationId',
    'setAppointmentInfo',
    'setAppointmentId',
  ]);
  const appointmentsQuery = AppointmentsApiQueries.useListPersonAppointments({
    locationId,
    personId,
  });
  const appointments = appointmentsQuery.data?.appointments ?? EMPTY_APPOINTMENTS;

  const selectAppointmentHandler = useCallback(
    (appointment: Appointment) => {
      setAppointmentInfo({
        appointmentAt: appointment.appointmentAt,
        pmsType: appointment.pmsType,
        practitioner: appointment.practitioner,
      });
      setAppointmentId(appointment?.appointmentId);
    },
    [setAppointmentInfo, setAppointmentId]
  );

  return (
    <>
      {appointments.length > 0 ? (
        <div css={appointmentsContainerStyle}>
          <Text color='light' css={textStyle}>
            {t("Link a form to recipient's upcoming appointment")}
          </Text>
          <AppointmentSelectorComponents.AppointmentsList
            appointments={appointments}
            onSelect={selectAppointmentHandler}
          />
        </div>
      ) : (
        <BannerNotification status='info' css={bannerNotificationStyle}>
          {t('No Upcoming Appointments')}
        </BannerNotification>
      )}
      <ContentLoader show={appointmentsQuery.isLoading} message={t('Loading appointments...')} />
    </>
  );
};

/**
 * Enhanced "Add More Details" step — renders appointment linking, an internal
 * note, and document attachment inside collapsible accordion sections.
 *
 * This is the NEW flow gated behind the `send-forms-enhanced-flow` wetag.
 * When the feature reaches 100% rollout, this replaces the original
 * `AppointmentSelectionStep`.
 */
const InternalNote: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const { setInternalNoteTitleError, setInternalNoteTitle, setInternalNote } = useSendFormsStore([
    'setInternalNoteTitleError',
    'setInternalNoteTitle',
    'setInternalNote',
  ]);
  const { getFieldProps } = useForm({
    fields: {
      internalNoteTitle: {
        type: 'text',
        value: '',
        placeholder: t('For Office Use Only'),
        maxLength: MAX_INTERNAL_NOTE_TITLE_LENGTH,
        validateOnChange: true,
        validator: ({ value }: ValidatorFieldState<'text'>) => {
          if (INVALID_INTERNAL_NOTE_TITLE_CHARACTERS_REGEX.test(value)) {
            return t(
              'Title contains invalid characters. Only letters, numbers, spaces and following special characters are allowed: hyphen (-), underscore (_), and parentheses ().'
            );
          }

          return '';
        },
      },
      internalNote: {
        type: 'text',
        value: '',
        maxLength: MAX_INTERNAL_NOTE_LENGTH,
      },
    },
  });
  const internalNoteTitleFieldProps = getFieldProps('internalNoteTitle');
  const internalNoteFieldProps = getFieldProps('internalNote');

  const note = internalNoteFieldProps.value ?? '';

  useEffect(() => {
    setInternalNoteTitleError(internalNoteTitleFieldProps.error || '');
  }, [internalNoteTitleFieldProps.error, setInternalNoteTitleError]);

  useEffect(() => {
    setInternalNoteTitle(internalNoteTitleFieldProps.value ?? '');
  }, [internalNoteTitleFieldProps.value, setInternalNoteTitle]);

  useEffect(() => {
    setInternalNote(note);
  }, [note, setInternalNote]);

  return (
    <>
      <EditableText
        helperText={internalNoteTitleFieldProps.error}
        fieldProps={internalNoteTitleFieldProps}
        alwaysShowIcon
        trackingId={pendoTags.internalNoteTitle}
        size='small'
        css={noteTitleStyle}
      />
      <AutoGrowTextarea
        id='send-forms-internal-note'
        data-trackingid={pendoTags.internalNote}
        name='internalNote'
        value={note}
        onChange={internalNoteFieldProps.onChange}
        onBlur={internalNoteFieldProps.onBlur}
        onFocus={internalNoteFieldProps.onFocus}
        aria-invalid={false}
        minRows={4}
        maxRows={6}
        css={noteInputStyle}
        aria-label={t('Add Internal Note')}
        maxLength={MAX_INTERNAL_NOTE_LENGTH}
        placeholder={t(
          'Add notes like Patient ID, Tooth Number, provider notes, insurance info or any reference details.'
        )}
      />
    </>
  );
};

type TileHeaderProps = {
  iconName: 'link' | 'note' | 'attachment';
  title: string;
  subTitle?: string;
  badge?: React.ReactNode;
  editableTitleProps?: BaseFieldProps;
};

const TileHeader: FC<TileHeaderProps> = ({ iconName, title, subTitle, badge }) => {
  return (
    <div css={tileHeaderContentStyle}>
      <Icon name={iconName} size={20} />
      <div css={titleStyle}>
        <div css={titleWithBadgeWrapperStyle}>
          <Text css={titleTextStyle} weight='bold'>
            {title}
          </Text>
          {badge}
        </div>

        {subTitle && (
          <Text color='subdued' css={{ textAlign: 'left' }} size='small'>
            {subTitle}
          </Text>
        )}
      </div>
    </div>
  );
};

const AddMoreDetailsStep: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');

  return (
    <Accordion
      shouldRenderBody
      css={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Accordion.Item value='link-appointment' css={accordionItemStyle} trackingId={pendoTags.linkAppointmentAccordion}>
        <Accordion.Header css={accordionHeaderStyle}>
          <TileHeader
            title={t('Link Appointment')}
            iconName='link'
            subTitle={t('Link an upcoming appointment to this form')}
          />
        </Accordion.Header>
        <Accordion.Body css={accordionBodyStyle(false)}>
          <Appointments />
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item
        value='add-internal-note'
        css={accordionItemStyle}
        trackingId={pendoTags.addInternalNoteAccordion}
      >
        <Accordion.Header css={accordionHeaderStyle}>
          <TileHeader
            title={t('Add Office Use Only Note')}
            badge={<BadgeBanner.AnimatedBadge key='new-badge' label={t('New')} />}
            iconName='note'
            subTitle={t(
              'Pre-fill details that patient can view but not edit. This note appears at the top of the form submission.'
            )}
          />
        </Accordion.Header>
        <Accordion.Body css={accordionBodyStyle(false)}>
          <InternalNote />
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item
        value='add-attachment-documents'
        css={accordionItemStyle}
        trackingId={pendoTags.addAttachmentDocumentsAccordion}
      >
        <Accordion.Header css={accordionHeaderStyle}>
          <TileHeader
            title={t('Add Additional Documents')}
            iconName='attachment'
            subTitle={t('Upload treatment plans or documents for patient review or e-signature.')}
          />
        </Accordion.Header>
        <Accordion.Body css={accordionBodyStyle(true)}>
          <AttachDocuments />
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default AddMoreDetailsStep;
